import React from 'react';
import { Link } from 'react-router-dom';
import { segmentForFamilyKey } from '../../config/familyRegistry';
import formatScalar from './formatValue';
import { getActiveSystem } from '../../config/system';
import displayFormatText from '../../utils/displayFormat';

/**
 * The datatype/format leaf of the generic renderer (05 §1.2).
 *
 * It renders ONE attribute's value according to its declared `datatype` and
 * `kind`, and nothing else — no entity knowledge, no per-family branch. The
 * number formats (`fraction`, `signed`, `locale`), the boolean ✓/✗ icon and the
 * `table` datatype arrive with the Families that declare them; the two converted
 * here declare `text`, `number` and one `reference`.
 *
 * A reference renders as a LINK when its target family has a route of its own,
 * and as plain text when it does not. That distinction is data, not code: a
 * lookup family such as `edition` carries `canonical_key: null`, so no
 * `canonical_family` row claims a URL segment for it and there is nowhere to
 * link to. It is the same "plain-badge degrade for a suppressed or unroutable
 * target" rule 05 §2.2 gives `badge-links`.
 */

/**
 * A reference's LINK DATA, by the rule stated once in `DetailSection`'s
 * `linkDataText`: the lowest-`display_order` field is the value, and every other
 * non-empty one is a parenthesized qualifier after it.
 *
 * It is repeated here rather than imported to avoid a cycle (DetailSection
 * imports this module), and the two are held identical by a test.
 *
 * WHY THIS EXISTS. The rule was only ever applied by the `link-list` section
 * style. A reference inside an ordinary `labeled-rows` group went through
 * `AttrValue`, which rendered the target's NAME and dropped everything on the
 * link — so a feat requiring "Dex 13" displayed "Dex", asserting a requirement
 * without stating it. Reported by a human parity tester on 2026-07-31 (#211);
 * neither automated instrument could see it, because production rendered that
 * row EMPTY and the DOM diff's `filled_empty_row` predicate correctly excuses
 * dev filling a row production left blank — it cannot tell a full fill from a
 * partial one.
 */
function linkDataSuffix(attr, target) {
  const fields = (attr.linkData || []).filter((f) => target[f.key] !== undefined
    && target[f.key] !== null && target[f.key] !== '');
  if (!fields.length) return '';
  const [primary, ...qualifiers] = fields;
  return ` ${target[primary.key]}${qualifiers.map((f) => ` (${target[f.key]})`).join('')}`;
}

function LinkOrText({ target, attr, from, listFrom }) {
  const segment = segmentForFamilyKey(attr.targetFamily, getActiveSystem());
  // A target that spells itself replaces the whole cell: the format is a
  // sentence about this link ("Base attack bonus +3"), not the target's name
  // with something appended, so there is no name left to hang a link on.
  const spelled = displayFormatText(attr, target);
  if (spelled !== null) return <>{spelled}</>;
  const label = target.name;
  // The qualifier rides OUTSIDE the link: the link addresses the target, and
  // "13" is a property of this feat's requirement, not part of Dex's name.
  const suffix = linkDataSuffix(attr, target);
  if (!segment || !target.slug) return <>{label}{suffix}</>;
  return (
    <>
      <Link
        to={`/${segment}?slug=${encodeURIComponent(target.slug)}`}
        state={{ from, listFrom }}
        data-preview={`${segment}/${target.slug}`}
      >
        {label}
      </Link>
      {suffix}
    </>
  );
}

export default function AttrValue({ attr, value, via, from, listFrom }) {
  if (value === undefined || value === null || value === '') return null;

  // A value that links through a reference beside it (issue 818,
  // `render_config.link_via`): the book's own words ("Mace (heavy)"), made the
  // link to the entry the reference resolved to ("Mace, heavy"). Unresolved,
  // the words stand alone. The target carries its URL segment as
  // `entity_type` (utils/entryLinks.js), so no Family lookup is needed.
  if (via && !Array.isArray(via) && via.slug && via.entity_type && !Array.isArray(value)) {
    return (
      <Link
        to={`/${via.entity_type}?slug=${encodeURIComponent(via.slug)}`}
        state={{ from, listFrom }}
        data-preview={`${via.entity_type}/${via.slug}`}
      >
        {formatScalar(attr, value)}
      </Link>
    );
  }

  // A REVERSE section's rows are the same shape as a forward reference's and
  // link to the same place — that is what `mirrors` asserts — so they render
  // through the same branch rather than a second one. The feats that require
  // Dodge are feats.
  if (attr.kind === 'reference' || attr.kind === 'reverse') {
    const targets = Array.isArray(value) ? value : [value];
    if (!targets.length) return null;
    return (
      <>
        {targets.map((target, i) => (
          <React.Fragment key={target.slug || i}>
            {i > 0 && ', '}
            <LinkOrText target={target} attr={attr} from={from} listFrom={listFrom} />
          </React.Fragment>
        ))}
      </>
    );
  }

  if (Array.isArray(value)) return <>{value.join(', ')}</>;
  // The number format and the unit are the ATTRIBUTE's (v2.6, gap G3), so this
  // row and the list card that links to it cannot spell one value two ways.
  return <>{formatScalar(attr, value)}</>;
}
